import maplibregl from 'maplibre-gl'
import {
  point,
  distance,
  bearing as turfBearing,
  lineString,
  length as turfLength,
  polygon,
  area,
  polygonToLine,
  circle as turfCircle,
} from '@turf/turf'
import {
  convertDistance,
  convertArea,
  formatBearing,
  type UnitSystem,
} from '../../../utils/units.js'
import {
  formatPosition,
  type CoordinateFormat,
  type CoordinatePrecision,
} from '../../../lib/coordinates.js'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'

export interface MeasureData {
  distance: string | undefined
  bearing: string | undefined
  area: string | undefined
  perimeter: string | undefined
  type: 'none' | 'line' | 'shape' | 'point'
  location: string | undefined
}

export interface MeasurementCallbacks {
  onMeasureUpdated: (data: MeasureData) => void
  onShapeSelected: (shapeId: string, selected: boolean) => void
  onMeasureAttribute: (attr: string, set: boolean) => void
  getUnitSystem: () => UnitSystem
  getCoordinateFormat: () => CoordinateFormat
  getCoordinatePrecision: () => CoordinatePrecision
  disableMapPanning: () => void
  enableMapPanning: () => void
}

export class MeasurementManager {
  private map?: maplibregl.Map

  private measurePoints: maplibregl.LngLat[] = []
  private measureSourceId = 'measure-line'
  private measureLayerId = 'measure-line-layer'
  private measurePreviewSourceId = 'measure-preview'
  private measurePreviewLayerId = 'measure-preview-layer'
  private measureMarkerElements: maplibregl.Marker[] = []
  private measureSelectedShapeId: string | undefined
  private measureMarkers: maplibregl.Marker[] | undefined
  private measurePreviewLayer: { sourceId: string; layerId: string } | undefined
  private measureLayer: { sourceId: string; layerId: string } | undefined

  constructor(private callbacks: MeasurementCallbacks) {}

  setMap(map: maplibregl.Map): void {
    this.map = map
  }

  get hasMeasuredFeature(): boolean {
    return !!this.measureSelectedShapeId || this.measurePoints.length === 2
  }

  handleClick(latlng: maplibregl.LngLat): void {
    if (this.hasMeasuredFeature) {
      this.cleanup()
      return
    }

    if (this.measurePoints.length === 0) {
      this.measurePoints = [latlng]
      this.callbacks.onMeasureUpdated({
        type: 'line',
        distance: undefined,
        bearing: undefined,
        area: undefined,
        perimeter: undefined,
        location: undefined,
      })
      this.callbacks.disableMapPanning()

      this.measureMarkers ??= []

      const markerElement = document.createElement('div')
      markerElement.style.width = '10px'
      markerElement.style.height = '10px'
      markerElement.style.backgroundColor = '#3f5a7a'
      markerElement.style.borderRadius = '50%'
      markerElement.style.border = '2px solid #3f5a7a'

      const marker = new maplibregl.Marker({
        element: markerElement,
        anchor: 'center',
      })
        .setLngLat([latlng.lng, latlng.lat])
        .addTo(this.map!)

      this.measureMarkerElements.push(marker)
    } else if (this.measurePoints.length === 1) {
      this.completeMeasureLine(latlng)
    }
  }

  updatePreview(mousePos: maplibregl.LngLat): void {
    if (this.measurePoints.length !== 1) {
      return
    }

    if (this.measurePreviewLayer && this.map) {
      this.map.removeLayer(this.measurePreviewLayerId)
      this.map.removeSource(this.measurePreviewSourceId)
    }

    const startPoint = this.measurePoints[0]
    const previewCoords = [
      [startPoint.lng, startPoint.lat],
      [mousePos.lng, mousePos.lat],
    ]

    if (this.map) {
      this.map.addSource(this.measurePreviewSourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: previewCoords,
          },
          properties: {},
        },
      })

      this.map.addLayer({
        id: this.measurePreviewLayerId,
        type: 'line',
        source: this.measurePreviewSourceId,
        paint: {
          'line-color': '#3f5a7a',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [4, 4],
        },
      })

      this.measurePreviewLayer = {
        sourceId: this.measurePreviewSourceId,
        layerId: this.measurePreviewLayerId,
      }
    }

    const from = point([startPoint.lng, startPoint.lat])
    const to = point([mousePos.lng, mousePos.lat])
    const distMeters = distance(from, to, { units: 'meters' })
    const bearingVal = turfBearing(from, to)

    this.callbacks.onMeasureUpdated({
      type: 'line',
      distance: convertDistance(distMeters, this.callbacks.getUnitSystem()),
      bearing: formatBearing(bearingVal),
      area: undefined,
      perimeter: undefined,
      location: undefined,
    })
  }

  private completeMeasureLine(latlng: maplibregl.LngLat): void {
    if (this.measurePoints.length === 0) {
      return
    }

    const startPoint = this.measurePoints[0]

    if (this.measurePreviewLayer && this.map) {
      this.map.removeLayer(this.measurePreviewLayerId)
      this.map.removeSource(this.measurePreviewSourceId)
      this.measurePreviewLayer = undefined
    }

    const lineCoords = [
      [startPoint.lng, startPoint.lat],
      [latlng.lng, latlng.lat],
    ]

    if (this.map) {
      this.map.addSource(this.measureSourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: lineCoords,
          },
          properties: {},
        },
      })

      this.map.addLayer({
        id: this.measureLayerId,
        type: 'line',
        source: this.measureSourceId,
        paint: {
          'line-color': '#3f5a7a',
          'line-width': 3,
          'line-opacity': 0.8,
        },
      })

      this.measureLayer = { sourceId: this.measureSourceId, layerId: this.measureLayerId }
    }

    const endMarkerElement = document.createElement('div')
    endMarkerElement.style.width = '10px'
    endMarkerElement.style.height = '10px'
    endMarkerElement.style.backgroundColor = '#3f5a7a'
    endMarkerElement.style.borderRadius = '50%'
    endMarkerElement.style.border = '2px solid #3f5a7a'

    const endMarker = new maplibregl.Marker({
      element: endMarkerElement,
      anchor: 'center',
    })
      .setLngLat([latlng.lng, latlng.lat])
      .addTo(this.map!)

    this.measureMarkerElements.push(endMarker)

    const from = point([startPoint.lng, startPoint.lat])
    const to = point([latlng.lng, latlng.lat])
    const distMeters = distance(from, to, { units: 'meters' })
    const bearingVal = turfBearing(from, to)

    this.measurePoints = [startPoint, latlng]
    this.callbacks.onMeasureAttribute('measure-shape-selected', true)
    this.callbacks.enableMapPanning()

    this.callbacks.onMeasureUpdated({
      type: 'line',
      distance: convertDistance(distMeters, this.callbacks.getUnitSystem()),
      bearing: formatBearing(bearingVal),
      area: undefined,
      perimeter: undefined,
      location: undefined,
    })
  }

  async handleShapeClick(shapeId: string): Promise<void> {
    const feature = await tripFeatureManager.getFeature(shapeId)
    if (!feature) {
      return
    }

    this.cleanup()

    this.measureSelectedShapeId = shapeId
    this.callbacks.onMeasureAttribute('measure-shape-selected', true)
    this.callbacks.onShapeSelected(shapeId, true)

    const unitSystem = this.callbacks.getUnitSystem()

    if (feature.type === 'point') {
      const coords = (feature.geometry as GeoJSON.Point).coordinates
      this.callbacks.onMeasureUpdated({
        type: 'point',
        location: formatPosition(
          { lat: coords[1], lng: coords[0] },
          this.callbacks.getCoordinateFormat(),
          this.callbacks.getCoordinatePrecision()
        ),
        distance: undefined,
        bearing: undefined,
        area: undefined,
        perimeter: undefined,
      })
    } else if (feature.type === 'line') {
      const line = lineString((feature.geometry as GeoJSON.LineString).coordinates)
      const distMeters = turfLength(line, { units: 'meters' })
      this.callbacks.onMeasureUpdated({
        type: 'line',
        distance: convertDistance(distMeters, unitSystem),
        bearing: undefined,
        area: undefined,
        perimeter: undefined,
        location: undefined,
      })
    } else if (feature.type === 'polygon' || feature.type === 'rectangle') {
      const poly = polygon((feature.geometry as GeoJSON.Polygon).coordinates)
      const areaM2 = area(poly)
      const perimeterM = turfLength(polygonToLine(poly), { units: 'meters' })
      this.callbacks.onMeasureUpdated({
        type: 'shape',
        area: convertArea(areaM2, unitSystem),
        perimeter: convertDistance(perimeterM, unitSystem),
        distance: undefined,
        bearing: undefined,
        location: undefined,
      })
    } else {
      const radius =
        feature.style?.radius ?? (feature.properties?.radius as number | undefined) ?? 100
      const center = (feature.geometry as GeoJSON.Point).coordinates
      const circleGeo = turfCircle(center, radius / 1000, { units: 'kilometers', steps: 64 })
      const areaM2 = area(circleGeo)
      const circumference = 2 * Math.PI * radius
      this.callbacks.onMeasureUpdated({
        type: 'shape',
        area: convertArea(areaM2, unitSystem),
        perimeter: convertDistance(circumference, unitSystem),
        distance: undefined,
        bearing: undefined,
        location: undefined,
      })
    }
  }

  cleanup(): void {
    if (this.measureSelectedShapeId) {
      this.callbacks.onShapeSelected(this.measureSelectedShapeId, false)
      this.measureSelectedShapeId = undefined
    }
    this.callbacks.onMeasureAttribute('measure-shape-selected', false)

    if (this.measureLayer && this.map) {
      this.map.removeLayer(this.measureLayer.layerId)
      this.map.removeSource(this.measureLayer.sourceId)
    }
    this.measureLayer = undefined

    if (this.measurePreviewLayer && this.map) {
      this.map.removeLayer(this.measurePreviewLayer.layerId)
      this.map.removeSource(this.measurePreviewLayer.sourceId)
    }
    this.measurePreviewLayer = undefined

    if (this.measureMarkers) {
      this.measureMarkerElements.forEach(marker => marker.remove())
      this.measureMarkerElements = []
    }
    this.measureMarkers = undefined

    this.measurePoints = []
    this.callbacks.enableMapPanning()

    this.callbacks.onMeasureUpdated({
      type: 'none',
      distance: undefined,
      bearing: undefined,
      area: undefined,
      perimeter: undefined,
      location: undefined,
    })
  }

  dispose(): void {
    this.cleanup()
  }
}
